import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, Avatar } from '../ui'
import { getNFLFantasyMatchup, getNFLFantasy } from '../api'

// Fantasy matchup hub — build your team and your opponent's, get each projected
// lineup total and the WIN PROBABILITY. Answers "will I win this week?"

const SLOTS = ['QB', 'RB', 'WR', 'TE', 'FLEX']
const ptsCol = p => p >= 18 ? T.green : p >= 12 ? T.mint : p >= 8 ? T.amber : T.muted2

function Lineup({ data, label, win }) {
  if (!data) return null
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', color: win ? T.green : T.muted, marginBottom: 6 }}>{label}</div>
      {data.lineup.map((s, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '4px 0', fontSize: 11.5 }}>
          <span style={{ color: T.muted, width: 34, flexShrink: 0 }}>{s.slot}</span>
          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: s.empty ? T.faint : T.text }}>
            {s.empty ? '—' : s.name.split(' ').slice(-1)[0]}</span>
          <span style={{ fontFamily: T.mono, color: s.empty ? T.faint : ptsCol(s.pts) }}>{s.empty ? '' : s.pts}</span>
        </div>
      ))}
    </div>
  )
}

export default function MatchupPanel({ season, week, scoring, me, opp, onMe, onOpp }) {
  const [res, setRes] = useState(null)
  const [pool, setPool] = useState(null)
  const [q, setQ] = useState('')
  const meIds = me.map(t => t.gsis_id), oppIds = opp.map(t => t.gsis_id)

  useEffect(() => {
    let alive = true; setRes(null)
    getNFLFantasyMatchup(meIds, oppIds, season, week, scoring).then(d => { if (alive) setRes(d) })
    return () => { alive = false }
  }, [meIds.join(','), oppIds.join(','), season, week, scoring])

  useEffect(() => {
    let alive = true
    getNFLFantasy(season, week, scoring, 'ALL', 300).then(d => { if (alive) setPool(d.rows || []) })
    return () => { alive = false }
  }, [season, week, scoring])

  const meSet = new Set(meIds), oppSet = new Set(oppIds)
  const wp = res && res.me_win_prob != null ? Math.round(res.me_win_prob * 100) : null
  const meWin = res && res.margin >= 0

  return (
    <div>
      {/* Head-to-head result */}
      {!res ? <Spinner /> : (
        <div style={{ background: T.card, border: `1px solid ${T.border2}`, borderRadius: 14, padding: 15, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 10.5, color: T.muted, letterSpacing: '0.05em' }}>MY TEAM</div>
              <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 30, color: meWin ? T.green : T.text }}>{res.me ? res.me.total : 0}</div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.faint }}>vs</div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 10.5, color: T.muted, letterSpacing: '0.05em' }}>OPPONENT</div>
              <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 30, color: !meWin ? T.green : T.text }}>{res.opp ? res.opp.total : 0}</div>
            </div>
          </div>
          {wp != null && (me.length > 0 || opp.length > 0) && (<>
            <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', background: T.card2, display: 'flex', marginTop: 6 }}>
              <div style={{ width: `${wp}%`, background: T.green }} />
              <div style={{ width: `${100 - wp}%`, background: T.border2 }} />
            </div>
            <div style={{ textAlign: 'center', fontSize: 12.5, color: T.muted, marginTop: 8 }}>
              Win probability <b style={{ color: T.green }}>{wp}%</b> · projected {meWin ? 'lead' : 'deficit'} <b style={{ color: T.text }}>{Math.abs(res.margin)}</b> pts
            </div>
          </>)}
          {me.length === 0 && opp.length === 0 && (
            <div style={{ fontSize: 11.5, color: T.muted, textAlign: 'center', marginTop: 8 }}>Add players to each side below to project the matchup.</div>
          )}
          {(me.length > 0 || opp.length > 0) && (
            <div style={{ display: 'flex', gap: 14, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
              <Lineup data={res.me} label="My lineup" win={meWin} />
              <div style={{ width: 1, background: T.border }} />
              <Lineup data={res.opp} label="Opp lineup" win={!meWin} />
            </div>
          )}
        </div>
      )}

      {/* Roster builder — tap Me / Opp per player */}
      <div style={{ fontSize: 12.5, fontWeight: 800, margin: '4px 2px 4px' }}>Build the rosters</div>
      <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 9 }}>
        <span style={{ color: T.green }}>{me.length} on my team</span> · <span style={{ color: T.mint }}>{opp.length} on opponent</span> — tap to add each side.
      </div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search players…"
        style={{ width: '100%', boxSizing: 'border-box', background: T.card2, border: `1px solid ${T.border}`,
          borderRadius: 10, padding: '10px 12px', color: T.text, fontSize: 13, marginBottom: 10, outline: 'none' }} />
      {(() => {
        const ql = q.trim().toLowerCase()
        const shown = ql ? (pool || []).filter(r => r.name.toLowerCase().includes(ql)) : (pool || []).slice(0, 60)
        return !pool ? <Spinner />
          : !pool.length ? <Empty message="Not available yet — this week's projections load midweek. Check back near kickoff." />
          : !shown.length ? <Empty message={`No players match “${q}”.`} />
          : shown.map(r => {
          const inMe = meSet.has(r.gsis_id), inOpp = oppSet.has(r.gsis_id)
          return (
            <div key={r.gsis_id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 11px',
              background: T.card, border: `1px solid ${inMe ? T.greenMid : inOpp ? `${T.mint}66` : T.border}`, borderRadius: 10, marginBottom: 6 }}>
              <Avatar url={r.headshot} name={r.name} team={r.team} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                <div style={{ fontSize: 10.5, color: T.muted }}>{r.pos} · {r.team} · {r.pts} proj</div>
              </div>
              <button onClick={() => onMe(r)} style={{ fontSize: 11, fontWeight: 800, cursor: 'pointer', padding: '5px 10px', borderRadius: 8,
                border: `1px solid ${T.green}`, background: inMe ? T.green : 'transparent', color: inMe ? '#04140D' : T.green }}>{inMe ? '✓' : 'Me'}</button>
              <button onClick={() => onOpp(r)} style={{ fontSize: 11, fontWeight: 800, cursor: 'pointer', padding: '5px 10px', borderRadius: 8,
                border: `1px solid ${T.mint}`, background: inOpp ? T.mint : 'transparent', color: inOpp ? '#04140D' : T.mint }}>{inOpp ? '✓' : 'Opp'}</button>
            </div>
          )
        })
      })()}
    </div>
  )
}
